/**
 * Earnings.jsx
 *
 * Subscription-based Earnings Dashboard:
 * - Passengers pay directly to drivers (Cash/Online)
 * - No payout system - drivers keep all fare
 * - Shows ride counts & fare collected by drivers
 */

import { useState, useEffect, useCallback } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";
import { toast } from "react-toastify";
import {
  FaRupeeSign,
  FaCar,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaSearch,
  FaDownload,
  FaSyncAlt,
  FaUsers,
  FaMoneyBillWave,
  FaCreditCard,
} from "react-icons/fa";
import AccessControl from "../components/AccessControl";
import {
  getEarningsSummary,
  getWeeklyChartData,
  getDriverPayouts,
} from "../api/earningsApi";
import Loader from "../components/Loader";


export default function Earnings() {
  // ✅ State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    today: { earnings: 0, rides: 0, change: 0, changeText: "" },
    week: { earnings: 0, rides: 0, change: 0, changeText: "" },
    month: { earnings: 0, rides: 0, change: 0, changeText: "" },
  });
  const [weeklyData, setWeeklyData] = useState([]);
  const [driverEarnings, setDriverEarnings] = useState([]);
  const [totals, setTotals] = useState({
    totalRides: 0,
    totalFare: 0,
    cashRides: 0,
    onlineRides: 0,
  });
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [tableLoading, setTableLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Fetch earnings summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await getEarningsSummary();
      if (res.success) {
        setStats(res.data);
      } else {
        toast.error(res.error || "Failed to fetch earnings summary");
      }
    } catch (err) {
      console.error("fetchSummary error:", err);
      toast.error("Something went wrong");
    }
  }, []);

  // ✅ Fetch weekly chart data
  const fetchWeeklyData = useCallback(async () => {
    try {
      const res = await getWeeklyChartData();
      if (res.success) {
        setWeeklyData(res.data);
      } else {
        toast.error(res.error || "Failed to fetch weekly data");
      }
    } catch (err) {
      console.error("fetchWeeklyData error:", err);
    }
  }, []);

  // ✅ Fetch driver earnings
  const fetchDriverEarnings = useCallback(async (date) => {
    try {
      setTableLoading(true);
      const res = await getDriverPayouts(date);
      if (res.success) {
        setDriverEarnings(res.payouts || []);
        setTotals({
          totalRides: res.totals?.totalRides || 0,
          totalFare: res.totals?.totalGross || 0,
          cashRides: res.totals?.cashRides || 0,
          onlineRides: res.totals?.onlineRides || 0,
        });
      } else {
        toast.error(res.error || "Failed to fetch driver earnings");
      }
    } catch (err) {
      console.error("fetchDriverEarnings error:", err);
    } finally {
      setTableLoading(false);
    }
  }, []);

  // ✅ Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchWeeklyData(), fetchDriverEarnings(selectedDate)]);
      setLoading(false);
    };
    loadData();
  }, [fetchSummary, fetchWeeklyData, fetchDriverEarnings, selectedDate]);

  // ✅ Handle date change
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    fetchDriverEarnings(newDate);
  };

  // ✅ Handle refresh
  const handleRefresh = async () => {
    toast.info("Refreshing data...");
    await Promise.all([fetchSummary(), fetchWeeklyData(), fetchDriverEarnings(selectedDate)]);
    toast.success("Data refreshed!");
  };

  // ✅ Handle export
  const handleExport = () => {
    toast.info("Export feature coming soon!");
  };

  // ✅ Get change icon
  const getChangeIcon = (change) => {
    if (change > 0) return <FaArrowUp size={10} />;
    if (change < 0) return <FaArrowDown size={10} />;
    return <FaMinus size={10} />;
  };

  // ✅ Filter drivers by search
  const filteredDrivers = driverEarnings.filter(
    (d) =>
      d.driver?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.driverId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ✅ Format currency
  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString("en-IN")}`;
  };

  // ✅ Payment mode pie data
  // const paymentPieData = [
  //   { name: "Cash", value: totals.cashRides || 0 },
  //   { name: "Online", value: totals.onlineRides || 0 },
  // ].filter(d => d.value > 0);

  if (loading) {
    return (
      <Loader />
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: 1400, margin: "0 auto" }}>
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN","HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />

      {/* ============ HEADER ============ */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 28,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: "#0f172a",
              marginBottom: 4,
              letterSpacing: "-0.5px",
            }}
          >
            Earnings Overview
          </h1>
          <p style={{ fontSize: 14, color: "#64748b" }}>
            Track rides, fare collection & driver performance
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={handleRefresh}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 18px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              background: "#fff",
              color: "#475569",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <FaSyncAlt size={13} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 18px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <FaDownload size={13} />
            Export
          </button>
        </div>
      </div>

      {/* ============ STAT CARDS ============ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 18,
          marginBottom: 28,
        }}
      >
        {/* Today */}
        <div
          style={{
            background: "linear-gradient(135deg, #059669, #10b981)",
            borderRadius: 16,
            padding: 22,
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaCalendarDay size={16} />
            </div>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Today's Fare</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>
            {formatCurrency(stats.today.earnings)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.9 }}>
            {getChangeIcon(stats.today.change)}
            <span>{stats.today.changeText || "vs yesterday"}</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
            🛺 {stats.today.rides || 0} rides
          </div>
        </div>

        {/* This Week */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb, #3b82f6)",
            borderRadius: 16,
            padding: 22,
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaCalendarWeek size={16} />
            </div>
            <span style={{ fontSize: 13, opacity: 0.9 }}>This Week</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>
            {formatCurrency(stats.week.earnings)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.9 }}>
            {getChangeIcon(stats.week.change)}
            <span>{stats.week.changeText || "vs last week"}</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
            🛺 {stats.week.rides || 0} rides
          </div>
        </div>

        {/* This Month */}
        <div
          style={{
            background: "linear-gradient(135deg, #d97706, #f59e0b)",
            borderRadius: 16,
            padding: 22,
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaCalendarAlt size={16} />
            </div>
            <span style={{ fontSize: 13, opacity: 0.9 }}>This Month</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>
            {formatCurrency(stats.month.earnings)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.9 }}>
            {getChangeIcon(stats.month.change)}
            <span>{stats.month.changeText || "vs last month"}</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
            🛺 {stats.month.rides || 0} rides
          </div>
        </div>

        {/* Avg Fare per Ride */}
        <div
          style={{
            background: "linear-gradient(135deg, #7c3aed, #a78bfa)",
            borderRadius: 16,
            padding: 22,
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaRupeeSign size={16} />
            </div>
            <span style={{ fontSize: 13, opacity: 0.9 }}>Avg Fare/Ride</span>
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>
            {stats.month.rides > 0
              ? formatCurrency(Math.round(stats.month.earnings / stats.month.rides))
              : "₹0"}
          </div>
          <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
            Based on this month
          </div>
        </div>
      </div>

      {/* ============ CHARTS ROW ============ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          marginBottom: 28,
        }}
      >
        {/* Weekly Revenue Chart */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            padding: 20,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>
              Fare Trend
            </h3>
            <p style={{ fontSize: 12, color: "#94a3b8" }}>Last 7 days</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={weeklyData}>
              <defs>
                <linearGradient id="fareGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v}`} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 12 }}
                formatter={(value) => [formatCurrency(value), "Fare"]}
              />
              <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2.5} fill="url(#fareGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Bookings Bar Chart */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            padding: 20,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>
              Daily Rides
            </h3>
            <p style={{ fontSize: 12, color: "#94a3b8" }}>Last 7 days</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weeklyData} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 12 }}
                formatter={(value) => [value, "Rides"]}
                cursor={{ fill: "#f8fafc" }}
              />
              <Bar dataKey="bookings" fill="#6366f1" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ============ DRIVER EARNINGS TABLE ============ */}
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        {/* Table Header */}
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>
              Driver Earnings
            </h3>
            <p style={{ fontSize: 12, color: "#94a3b8" }}>
              Fare collected by drivers from passengers
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Search */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "#f8fafc",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                padding: "8px 12px",
                gap: 8,
              }}
            >
              <FaSearch size={13} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search driver..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  border: "none",
                  background: "none",
                  outline: "none",
                  fontSize: 13,
                  width: 140,
                  color: "#0f172a",
                }}
              />
            </div>
            {/* Date Picker */}
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                fontSize: 13,
                color: "#0f172a",
                background: "#f8fafc",
                cursor: "pointer",
              }}
            />
          </div>
        </div>

        {/* Table Content */}
        {tableLoading ? (
          <div style={{ padding: 50, textAlign: "center", color: "#94a3b8" }}>
            <div
              style={{
                width: 36,
                height: 36,
                border: "3px solid #e2e8f0",
                borderTopColor: "#6366f1",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 14px",
              }}
            />
            Loading...
          </div>
        ) : filteredDrivers.length === 0 ? (
          <div style={{ padding: 50, textAlign: "center" }}>
            <div
              style={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 14px",
                fontSize: 28,
              }}
            >
              🛺
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#475569", marginBottom: 2 }}>
              No Rides Found
            </p>
            <p style={{ fontSize: 12, color: "#94a3b8" }}>
              No completed rides on this date
            </p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Driver</th>
                    <th style={thStyle}>Vehicle</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>Rides</th>
                    <th style={thStyle}>Total Fare</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>Cash</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>Online</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>Avg Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDrivers.map((d, i) => (
                    <tr key={d.driverId || i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={tdStyle}>
                        <span style={{ color: "#94a3b8", fontWeight: 600, fontSize: 12 }}>{i + 1}</span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {d.photo ? (
                            <img
                              src={d.photo}
                              alt={d.driver}
                              style={{ width: 38, height: 38, borderRadius: 10, objectFit: "cover" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: "#e0e7ff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 16,
                              }}
                            >
                              🛺
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 13 }}>{d.driver}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>{d.phone || d.driverId}</div>
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            background: "#f1f5f9",
                            padding: "4px 8px",
                            borderRadius: 5,
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#475569",
                          }}
                        >
                          {d.vehicleNumber || "N/A"}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{d.rides}</span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 700, color: "#059669", fontSize: 14 }}>
                          {formatCurrency(d.gross)}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                          <FaMoneyBillWave size={12} color="#10b981" />
                          <span style={{ fontWeight: 600, color: "#10b981", fontSize: 13 }}>
                            {d.cashRides || 0}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                          <FaCreditCard size={12} color="#6366f1" />
                          <span style={{ fontWeight: 600, color: "#6366f1", fontSize: 13 }}>
                            {d.onlineRides || 0}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <span style={{ color: "#f59e0b", fontWeight: 600, fontSize: 13 }}>
                          ⭐ {d.avgRating || "N/A"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals Footer */}
            <div
              style={{
                padding: "18px 22px",
                background: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", gap: 28 }}>
                <div>
                  <span style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 2 }}>
                    Total Rides
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
                    {totals.totalRides}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 2 }}>
                    Total Drivers
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
                    {filteredDrivers.length}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 2 }}>
                    Cash Rides
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "#10b981" }}>
                    {totals.cashRides || 0}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 2 }}>
                    Online Rides
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "#6366f1" }}>
                    {totals.onlineRides || 0}
                  </span>
                </div>
              </div>
              <div
                style={{
                  background: "linear-gradient(135deg, #059669, #10b981)",
                  padding: "14px 22px",
                  borderRadius: 12,
                  color: "#fff",
                }}
              >
                <span style={{ fontSize: 11, opacity: 0.9, display: "block", marginBottom: 2 }}>
                  Total Fare Collected
                </span>
                <span style={{ fontSize: 22, fontWeight: 800 }}>
                  {formatCurrency(totals.totalFare)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ============ QUICK STATS ============ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 14,
          marginTop: 20,
        }}
      >
        <div style={quickStatStyle}>
          <div style={{ ...quickStatIconStyle, background: "#dcfce7" }}>
            <FaCar size={18} color="#16a34a" />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Today's Rides</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{stats.today.rides || 0}</div>
          </div>
        </div>

        <div style={quickStatStyle}>
          <div style={{ ...quickStatIconStyle, background: "#dbeafe" }}>
            <FaCar size={18} color="#2563eb" />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Week's Rides</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{stats.week.rides || 0}</div>
          </div>
        </div>

        <div style={quickStatStyle}>
          <div style={{ ...quickStatIconStyle, background: "#fef3c7" }}>
            <FaUsers size={18} color="#d97706" />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Active Drivers</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{filteredDrivers.length}</div>
          </div>
        </div>

        <div style={quickStatStyle}>
          <div style={{ ...quickStatIconStyle, background: "#f3e8ff" }}>
            <FaRupeeSign size={18} color="#9333ea" />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Avg per Ride</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
              {totals.totalRides > 0 ? formatCurrency(Math.round(totals.totalFare / totals.totalRides)) : "₹0"}
            </div>
          </div>
        </div>
      </div>

      {/* ============ CSS ============ */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ✅ Styles
const thStyle = {
  padding: "12px 14px",
  textAlign: "left",
  fontSize: 11,
  fontWeight: 700,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const tdStyle = {
  padding: "14px",
  fontSize: 13,
  color: "#475569",
};

const quickStatStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  alignItems: "center",
  gap: 14,
};

const quickStatIconStyle = {
  width: 44,
  height: 44,
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
